import { useBackendApi } from './backendApi';
import { useEventsPublisher, useEventsSubscriber } from 'event-bus';
import { useCacheService } from './cacheService';
import { Permission } from './Types';

export enum TasksGroupPermission {
  DELETE = 'DELETE',
  UPDATE = 'UPDATE',
  UPDATE_NAME = 'UPDATE_NAME',
}

export interface TasksGroup {
  id: number;
  name: string;
  color: string;
  isHidden: boolean;
  _permissions: TasksGroupPermission[]
}

export interface BaseTaskGroupRequest {
  name: string;
  color: string;
  isHidden: boolean;
}

export interface CreateTaskGroupRequest  extends BaseTaskGroupRequest {

}

export interface UpdateTaskGroupRequest extends BaseTaskGroupRequest {

}

export const TASKS_GROUP_CREATED = 'TASKS_GROUP_CREATED';
export const TASKS_GROUP_UPDATED = 'TASKS_GROUP_UPDATED';
export const TASKS_GROUP_DELETED = 'TASKS_GROUP_DELETED';

const ALL_GROUPS_CACHE_KEY = 'tasksGroups';

export function useTasksGroupsService() {
  const backendApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const cacheService = useCacheService('all');

  useEventsSubscriber('tasksGroupsService', {
    [TASKS_GROUP_CREATED]: () => cacheService.clear(ALL_GROUPS_CACHE_KEY),
    [TASKS_GROUP_UPDATED]: () => cacheService.clear(ALL_GROUPS_CACHE_KEY),
    [TASKS_GROUP_DELETED]: () => cacheService.clear(ALL_GROUPS_CACHE_KEY),
  })

  const getTasksGroups = async (showHidden = false) => {

    const allFields = await cacheService.get(ALL_GROUPS_CACHE_KEY, async (cacheItem) => {
      cacheItem.setExpireInSeconds(60);

      let result= await backendApi.get('/tasksgroups?returnHidden=1') as TasksGroup[];
      result = result.sort((a, b) => a.name.localeCompare(b.name));
      return result;
    });


    if (!showHidden) {
      return allFields.filter((group) => !group.isHidden);
    } else {
      return allFields;
    }
  };

  const createTaskGroup = async (request: CreateTaskGroupRequest) => {
    const result = await backendApi.post('/tasksgroups', request) as TasksGroup;
    eventsPublisher.publish(TASKS_GROUP_CREATED, {
      id: result.id,
    });

    return result;
  };

  const updateTaskGroup = async (id: number, request: CreateTaskGroupRequest) => {
    const result = await backendApi.put(`/tasksgroups/${id}`, request) as TasksGroup;

    eventsPublisher.publish(TASKS_GROUP_UPDATED, {
      id,
    })

    return result;
  };

  const deleteTaskGroup = async (id: number) => {
    await backendApi.delete(`/tasksgroups/${id}`);

    eventsPublisher.publish(TASKS_GROUP_DELETED, {
      id,
    })
  };

  return {
    getTasksGroups,
    createTaskGroup,
    updateTaskGroup,
    deleteTaskGroup,
  };
}
